/**
 * The command center's one period control, after shadcn's Date Range
 * Picker: an outline button with the chosen days, opening a popover with the
 * presets on the left and a two-month range calendar on the right. A preset
 * applies at once; a range drawn on the calendar applies with "Apply".
 *
 * @module components/commandCenter/CommandCenterDatePicker
 */
import type { DateRange } from "@daypicker/react";
import { CalendarIcon, CheckIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import {
  PERIOD_PRESETS,
  matchingPreset,
  periodLabel,
  presetPeriod,
  selectionPeriod,
  todayKey,
  type CommandCenterPeriod,
} from "./commandCenterPeriod";

/** A calendar day (local midnight, as the calendar works) for a `YYYY-MM-DD` key. */
const toDate = (day: string): Date => {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, date ?? 1);
};

/** The `YYYY-MM-DD` key of a calendar day. */
const toKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * The selected range in the theme accent, its middle a soft wash of it. The
 * popover portals out of the page, so the token is named here, not inherited.
 */
const RANGE_CLASSES = {
  day_button:
    "in-data-selected:bg-[var(--brand-accent,var(--primary))] in-data-selected:text-white in-[.range-middle]:in-data-selected:bg-[color-mix(in_oklab,var(--brand-accent,var(--primary))_16%,transparent)] in-[.range-middle]:in-data-selected:text-foreground",
};

export function CommandCenterDatePicker({
  period,
  onChange,
}: {
  readonly period: CommandCenterPeriod;
  readonly onChange: (period: CommandCenterPeriod) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const today = todayKey();
  const activePreset = period.preset ?? matchingPreset(period, today);
  const selected: DateRange = draft ?? { from: toDate(period.from), to: toDate(period.to) };
  const choice = useMemo(
    () =>
      selectionPeriod(
        selected.from ? toKey(selected.from) : null,
        selected.to ? toKey(selected.to) : null,
        today,
      ),
    [selected.from, selected.to, today],
  );
  // Far enough back for any two-year window of recent history.
  const earliest = toDate(today);
  earliest.setFullYear(earliest.getFullYear() - 4);

  const close = (next: boolean) => {
    setOpen(next);
    if (!next) setDraft(undefined);
  };

  return (
    <Popover open={open} onOpenChange={close}>
      <PopoverTrigger
        render={
          <Button
            size="sm"
            variant="outline"
            className="justify-start gap-2 font-normal tabular-nums"
            aria-label={`Period: ${periodLabel(period)}`}
          />
        }
      >
        <CalendarIcon aria-hidden className="size-4 text-muted-foreground" />
        {periodLabel(period)}
      </PopoverTrigger>
      <PopoverPopup align="end" aria-label="Choose a period" className="max-w-[calc(100vw-1rem)]">
        <div className="flex flex-col gap-3 sm:flex-row">
          <ul className="flex shrink-0 flex-row flex-wrap gap-1 sm:w-40 sm:flex-col sm:border-e sm:border-border/60 sm:pe-3">
            {PERIOD_PRESETS.map((preset) => {
              const active = draft === undefined && activePreset === preset.id;
              return (
                <li key={preset.id}>
                  <button
                    type="button"
                    data-preset={preset.id}
                    aria-pressed={active}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "font-medium text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                    onClick={() => {
                      onChange(presetPeriod(preset.id, today));
                      close(false);
                    }}
                  >
                    {preset.label}
                    {active ? <CheckIcon aria-hidden className="size-4" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-col gap-3">
            <Calendar
              mode="range"
              numberOfMonths={2}
              showOutsideDays={false}
              // Two months ending with the last chosen day's month.
              defaultMonth={
                new Date(toDate(period.to).getFullYear(), toDate(period.to).getMonth() - 1, 1)
              }
              startMonth={earliest}
              endMonth={toDate(today)}
              selected={selected}
              onSelect={(range) => setDraft(range ?? { from: undefined, to: undefined })}
              disabled={{ after: toDate(today) }}
              classNames={RANGE_CLASSES}
            />
            <div className="flex items-center justify-between gap-3 border-t border-border/60 pt-3">
              <p className="min-w-0 text-[12px] text-muted-foreground" role="status">
                {choice.ok ? periodLabel(choice.period) : choice.reason}
              </p>
              <div className="flex shrink-0 gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => close(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  disabled={!choice.ok || draft === undefined}
                  onClick={() => {
                    if (!choice.ok) return;
                    onChange(choice.period);
                    close(false);
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}
